/**
 * SPA hooks for /api/product-flags — Phase 1.9 Pass 4.
 *
 * Mirrors the keepaTemplates / keepaSchedules pattern: queryOptions for
 * cacheable reads, useMutation for state-changing operations with
 * onSuccess-driven cache invalidation.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BulkDismissFlagsRequest,
  BulkDismissFlagsResponse,
  ListProductFlagsResponse,
  ProductFlagsCountResponse,
  ReviewProductFlagRequest,
  ReviewProductFlagResponse,
  type ListProductFlagsQuery,
  type ProductFlagsCountQuery,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export type ProductFlagsParams = Partial<ListProductFlagsQuery>;

export function productFlagsQuery(params: ProductFlagsParams = {}) {
  const search = new URLSearchParams();
  if (params.flag) search.set('flag', params.flag);
  if (params.decision) search.set('decision', params.decision);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();

  return queryOptions({
    queryKey: [
      'product-flags',
      params.flag ?? 'any',
      params.decision ?? 'any',
      params.limit ?? 100,
    ] as const,
    queryFn: () =>
      apiFetch<ListProductFlagsResponse>(`/api/product-flags${qs ? `?${qs}` : ''}`, {
        responseSchema: ListProductFlagsResponse,
      }),
    staleTime: 30_000,
  });
}

export function useProductFlags(params: ProductFlagsParams = {}) {
  return useQuery(productFlagsQuery(params));
}

export type ProductFlagsCountParams = Partial<ProductFlagsCountQuery>;

export function productFlagsCountQuery(params: ProductFlagsCountParams = {}) {
  const search = new URLSearchParams();
  if (params.flag) search.set('flag', params.flag);
  if (params.decision) search.set('decision', params.decision);
  const qs = search.toString();

  return queryOptions({
    queryKey: [
      'product-flags',
      'count',
      params.flag ?? 'any',
      params.decision ?? 'pending',
    ] as const,
    queryFn: () =>
      apiFetch<ProductFlagsCountResponse>(`/api/product-flags/count${qs ? `?${qs}` : ''}`, {
        responseSchema: ProductFlagsCountResponse,
      }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useProductFlagsCount(params: ProductFlagsCountParams = {}) {
  return useQuery(productFlagsCountQuery(params));
}

export function useReviewProductFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'promoted' | 'dismissed' }) =>
      apiFetch<ReviewProductFlagResponse>(`/api/product-flags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ decision } satisfies ReviewProductFlagRequest),
        responseSchema: ReviewProductFlagResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-flags'] });
      qc.invalidateQueries({ queryKey: ['products'] }); // promote flips status
    },
  });
}

export function useBulkDismissFlags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkDismissFlagsRequest) =>
      apiFetch<BulkDismissFlagsResponse>(`/api/product-flags/bulk-dismiss`, {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: BulkDismissFlagsResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-flags'] });
    },
  });
}
